/**
 * Billing API service (T7) — the un-gated plan list plus the Stripe
 * checkout/portal contract.
 *
 * GET /api/billing/plans mirrors backend/src/api/routes/billing.py's
 * PlansListResponse — deliberately NOT the admin-gated /api/admin/plans
 * shape (types/plans.ts's Plan, with a required `features` array and
 * pagination envelope); this is the un-gated, tenant-facing list every
 * plan (free included) can read.
 *
 * POST /api/billing/stripe/checkout and /stripe/portal are being built by
 * a parallel lane (T4) — this file codes against the documented contract:
 *   POST /api/billing/stripe/checkout {plan_id, interval} -> {checkout_url}
 *   POST /api/billing/stripe/portal   {}                  -> {portal_url}
 * A 503 response (Stripe not configured yet) is surfaced as
 * BillingApiError with `status === 503` so callers can show one clear
 * banner regardless of the exact error code the parallel lane lands on.
 */

import { createHeaders } from './apiClient';
import type { ApiErrorOptions, HandleResponseOptions } from './httpResponse';
import { ApiError, handleResponse } from './httpResponse';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface BillingPlan {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  price_monthly_cents: number | null;
  price_yearly_cents: number | null;
  is_active: boolean;
}

export interface BillingPlansResponse {
  plans: BillingPlan[];
}

export type BillingInterval = 'monthly' | 'yearly';

export interface CheckoutResponse {
  checkout_url: string;
}

export interface PortalResponse {
  portal_url: string;
}

/** Billing's named failure. Callers do `err instanceof BillingApiError`
 * and read `.status` / `.code`; those reads are unchanged — only the
 * constructor's third argument moved from a positional `code` to the
 * shared `{ code, detail }` options bag. */
export class BillingApiError extends ApiError {
  constructor(message: string, status: number, options?: ApiErrorOptions) {
    super(message, status, options);
    this.name = 'BillingApiError';
  }
}

const billingErrors: HandleResponseOptions = { ErrorClass: BillingApiError };

export async function getBillingPlans(): Promise<BillingPlansResponse> {
  const response = await fetch(`${API_BASE_URL}/api/billing/plans`, {
    method: 'GET',
    headers: createHeaders(),
  });
  return handleResponse<BillingPlansResponse>(response, billingErrors);
}

export async function createStripeCheckout(
  planId: string,
  interval: BillingInterval
): Promise<CheckoutResponse> {
  const response = await fetch(`${API_BASE_URL}/api/billing/stripe/checkout`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify({ plan_id: planId, interval }),
  });
  return handleResponse<CheckoutResponse>(response, billingErrors);
}

export async function createStripePortalSession(): Promise<PortalResponse> {
  const response = await fetch(`${API_BASE_URL}/api/billing/stripe/portal`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify({}),
  });
  return handleResponse<PortalResponse>(response, billingErrors);
}
